import { useTranslation } from 'react-i18next';
import { tiktokCheckingFailedImage, tiktokCheckingImage } from '@anytag/shared-assets';
import {
  Icon,
  Button,
  BaseModal,
  ModalBody,
  ModalActions,
  MediaPreview,
  BaseModalProps,
  CircularProgress
} from '@anytag/shared-atoms';

export interface CheckingPlanSubscriptionModalProps extends Pick<BaseModalProps, 'open'> {
  retrying?: boolean;
  refreshing?: boolean;
  onRetry?: () => void;
  onCancel?: () => void;
  onRefresh?: () => void;
  type: 'checking' | 'failed' | 'success' | 'unknown';
}

export const CheckingPlanSubscriptionModal = ({
  type,
  onRetry,
  onCancel,
  onRefresh,
  retrying,
  refreshing,
  ...restProps
}: CheckingPlanSubscriptionModalProps) => {
  const { t } = useTranslation();

  const { icon, title, image, actions, description } = (() => {
    switch (type) {
      case 'failed':
        return {
          title: t('Payment Failed'),
          image: tiktokCheckingFailedImage,
          description: t('failedCampaignDescription'),
          icon: <Icon size={16} icon="fail" variant="red" />,
          actions: (
            <>
              <Button onClick={onCancel} variant="outline">
                {t('Button.Cancel')}
              </Button>
              <Button onClick={onRetry} variant="solid" loading={{ showIcon: true, status: retrying }}>
                {t('Button.Try Again')}
              </Button>
            </>
          )
        };
      case 'unknown':
        return {
          image: tiktokCheckingImage,
          title: t('Campaign Creating'),
          description: t('Please refresh'),
          icon: <CircularProgress size={16} thickness={0.5} className="bg-blue-70" />,
          actions: (
            <Button variant="solid" onClick={onRefresh} loading={{ showIcon: true, status: refreshing }}>
              {t('Button.Refresh')}
            </Button>
          )
        };
      case 'checking':
      default:
        return {
          actions: null,
          image: tiktokCheckingImage,
          title: t('Campaign Creating'),
          description: t('creatingCampaignDescription'),
          icon: <CircularProgress size={16} thickness={0.5} className="bg-blue-70" />
        };
    }
  })();

  return (
    <BaseModal {...restProps} asChild>
      <ModalBody className="p-0">
        <div className="mx-auto h-43.5 w-96.5 content-center rounded-lg bg-light-gray">
          <MediaPreview className="mx-auto my-0 h-42.25 w-35 bg-transparent" src={image} />
        </div>
        <div className="mx-auto mt-2 h-4 w-max">{icon}</div>
        <p className="mt-2 text-center text-lg leading-none font-semibold">{title}</p>
        <p className="mt-2 text-center text-sm leading-none text-tertiary">{description}</p>
      </ModalBody>

      {actions && <ModalActions>{actions}</ModalActions>}
    </BaseModal>
  );
};
